import { action, makeObservable, observable, runInAction } from 'mobx';
import type { RootStore } from 'logic';
import {
  FirestoreAccessRequest,
  AccessRequestStatus,
  SessionUserPermission,
} from '@creative-kit/shared';
import type ft from 'firebase';
import { db } from './data';

export class UiAccessRequestStore {
  private rootStore: RootStore;

  // Observable state
  public accessRequests: Record<string, FirestoreAccessRequest> = {};
  public pendingRequests: FirestoreAccessRequest[] = [];
  public isLoading = false;
  public error: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable<
      UiAccessRequestStore,
      'accessRequests' | 'pendingRequests' | 'isLoading' | 'error'
    >(this, {
      accessRequests: observable,
      pendingRequests: observable,
      isLoading: observable,
      error: observable,
      loadAccessRequests: action.bound,
      createAccessRequest: action.bound,
      approveAccessRequest: action.bound,
      denyAccessRequest: action.bound,
      clearError: action.bound,
    });
  }

  init() {
    this.loadAccessRequests();
  }

  async loadAccessRequests() {
    const { user } = this.rootStore.authStore;
    if (!user) return;

    this.isLoading = true;
    this.error = null;

    try {
      const userRequestsQuery = db.accessRequests
        .where('userId', '==', user.uid)
        .orderBy('requestedAt', 'desc');

      const userRequestsSnapshot = await userRequestsQuery.get();
      const userRequests: Record<string, FirestoreAccessRequest> = {};

      userRequestsSnapshot.forEach((doc: ft.firestore.QueryDocumentSnapshot) => {
        const requestData = doc.data();
        if (requestData) {
          userRequests[doc.id] = requestData as FirestoreAccessRequest;
        }
      });

      runInAction(() => {
        this.accessRequests = userRequests;
        this.pendingRequests = Object.values(userRequests).filter(
          (request) => request.status === AccessRequestStatus.PENDING,
        );
      });
    } catch (error) {
      runInAction(() => {
        this.error = 'Failed to load access requests';
        console.error('Error loading access requests:', error);
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async createAccessRequest({
    sessionId,
    sessionName,
    requestedPermission,
    message,
  }: {
    sessionId: string;
    sessionName: string;
    requestedPermission: SessionUserPermission;
    message?: string;
  }) {
    const { user } = this.rootStore.authStore;
    if (!user) {
      throw new Error('User must be authenticated to create access request');
    }

    this.isLoading = true;
    this.error = null;

    try {
      const requestData: FirestoreAccessRequest = {
        userId: user.uid,
        email: user.email || '',
        name: user.displayName || user.email || 'Unknown User',
        sessionId,
        sessionName,
        requestedPermission,
        status: AccessRequestStatus.PENDING,
        requestedAt: new Date(),
        message,
      };

      const docRef = await db.accessRequests.add(requestData);

      runInAction(() => {
        this.accessRequests[docRef.id] = requestData;
        this.pendingRequests.push(requestData);
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Access request sent successfully',
      });

      return docRef.id;
    } catch (error) {
      runInAction(() => {
        this.error = 'Failed to create access request';
        console.error('Error creating access request:', error);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async approveAccessRequest(requestId: string, responseMessage?: string) {
    const { user } = this.rootStore.authStore;
    if (!user) {
      throw new Error('User must be authenticated to approve access request');
    }

    this.isLoading = true;
    this.error = null;

    try {
      const request = this.accessRequests[requestId];
      if (!request) {
        throw new Error('Access request not found');
      }

      await db.accessRequests.doc(requestId).update({
        status: AccessRequestStatus.APPROVED,
        respondedAt: new Date(),
        respondedBy: {
          userId: user.uid,
          email: user.email,
        },
        responseMessage,
      });

      runInAction(() => {
        this.accessRequests[requestId] = {
          ...request,
          status: AccessRequestStatus.APPROVED,
          respondedAt: new Date(),
          respondedBy: {
            userId: user.uid,
            email: user.email,
          },
          responseMessage,
        };

        this.pendingRequests = this.pendingRequests.filter(
          (req) => req.userId !== request.userId || req.sessionId !== request.sessionId,
        );
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: `Access request approved for ${request.email}`,
      });
    } catch (error) {
      runInAction(() => {
        this.error = 'Failed to approve access request';
        console.error('Error approving access request:', error);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async denyAccessRequest(requestId: string, responseMessage?: string) {
    const { user } = this.rootStore.authStore;
    if (!user) {
      throw new Error('User must be authenticated to deny access request');
    }

    this.isLoading = true;
    this.error = null;

    try {
      const request = this.accessRequests[requestId];
      if (!request) {
        throw new Error('Access request not found');
      }

      await db.accessRequests.doc(requestId).update({
        status: AccessRequestStatus.DENIED,
        respondedAt: new Date(),
        respondedBy: {
          userId: user.uid,
          email: user.email,
        },
        responseMessage,
      });

      runInAction(() => {
        this.accessRequests[requestId] = {
          ...request,
          status: AccessRequestStatus.DENIED,
          respondedAt: new Date(),
          respondedBy: {
            userId: user.uid,
            email: user.email,
          },
          responseMessage,
        };

        this.pendingRequests = this.pendingRequests.filter(
          (req) => req.userId !== request.userId || req.sessionId !== request.sessionId,
        );
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: `Access request denied for ${request.email}`,
      });
    } catch (error) {
      runInAction(() => {
        this.error = 'Failed to deny access request';
        console.error('Error denying access request:', error);
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  clearError() {
    this.error = null;
  }

  getRequestsForSession(sessionId: string): FirestoreAccessRequest[] {
    return Object.values(this.accessRequests).filter((request) => request.sessionId === sessionId);
  }

  getPendingRequestsForSession(sessionId: string): FirestoreAccessRequest[] {
    return Object.values(this.accessRequests).filter(
      (request) =>
        request.sessionId === sessionId && request.status === AccessRequestStatus.PENDING,
    );
  }

  getUserRequests(): FirestoreAccessRequest[] {
    const { user } = this.rootStore.authStore;
    if (!user) return [];

    return Object.values(this.accessRequests).filter((request) => request.userId === user.uid);
  }
}
